import { NGXLogger } from 'ngx-logger';
import { resetOfflineIdentityVerified } from '../offline/offline-db';
import { cancelOfflineSync } from '../offline/offline-sync-control';
import { noteAnswered, takePrefilled } from '../offline/prefill-store';
import { Injectable, NgZone, inject } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, NEVER, Observable, defer, of, throwError, filter, timer } from 'rxjs';
import { catchError, finalize, retry, shareReplay, switchMap, tap, timeout } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { NavigationEnd, Router } from '@angular/router';
import { SessionExpiredDialogService } from '../services/session-expired-dialog.service';
import { ForcedLogoutDialogService } from '../services/forced-logout-dialog.service';
import { AccessDeniedDialogService } from '../services/access-denied-dialog.service';
import { PlanLimitDialogService } from '../services/plan-limit-dialog.service';
import { MessageService } from 'primeng/api';
import { ErrorReportService } from '../services/error-report.service';
import { ServerErrorDialogService } from '../services/server-error-dialog.service';
import { NetworkStatusService } from '../services/network-status.service';
import { TranslationService } from '../i18n/translation.service';
import { HttpActivityService } from '../services/http-activity.service';
import { AppConstants } from '../commons/app-constants';
import { clientDeviceHeaders } from '../commons/utils/client-device';
import { PushNotificationService } from '../services/push-notification.service';
import { QUIET_PLAN_BLOCK } from './quiet-plan-block';

@Injectable()
export class TallysheetHttpInterceptorService implements HttpInterceptor {
  private readonly logger = inject(NGXLogger);

  private refreshRequest$: Observable<any> | null = null;
  private sessionExpiredDialogVisible = false;
  private refreshJustSucceeded = false;

  // True once the router commits to a protected (non-login, non-public) route for
  // the first time. Until that happens every 401 is a "not logged in" probe from a
  // component that rendered before the router established context — there is nothing
  // to refresh, so we pass these through silently rather than attempting a token
  // refresh that will always fail and falsely trigger the Session Expired dialog.
  private authenticatedRouteEstablished = false;

  // Instance field, not a local in intercept() — handleError() is now called from two places
  // (the offline fast-fail path and the normal next.handle() path) and needs to share this
  // flag across both; the interceptor itself is a singleton so this is safe.
  private isRedirecting = false;

  // Separate from isRedirecting (307) — a blocked-dashboard page can fire several API calls
  // concurrently, each independently 402ing; without this, the user would get hit with a pile
  // of redirects/toasts instead of exactly one.
  private isRedirectingForSubscription = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private ngZone: NgZone,
    private sessionExpiredDialogService: SessionExpiredDialogService,
    private forcedLogoutDialogService: ForcedLogoutDialogService,
    private accessDeniedDialogService: AccessDeniedDialogService,
    private planLimitDialogService: PlanLimitDialogService,
    private messageService: MessageService,
    private errorReportService: ErrorReportService,
    private serverErrorDialogService: ServerErrorDialogService,
    private networkStatusService: NetworkStatusService,
    private translationService: TranslationService,
    private httpActivityService: HttpActivityService,
    private pushNotificationService: PushNotificationService,
  ) {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        const url: string = ((e.urlAfterRedirects || e.url) as string).split('?')[0].split('#')[0];
        if (!this.isRouteSuppressedForSessionDialog(url)) {
          this.authenticatedRouteEstablished = true;
        }
      });
  }

  // Long-running/large-payload endpoints (export, import, report generation, document
  // content, bundles) are excluded from the 2G resilience layer below — a 30s timeout
  // would abort a legitimately slow-but-succeeding request on these routes.
  private static readonly LONG_RUNNING_URL_PATTERN =
    /\/(export|import|report|bundle|download|documents\/[^/]+\/content|photos|zip)\b/i;

  private isLongRunningRequest(req: HttpRequest<any>): boolean {
    return TallysheetHttpInterceptorService.LONG_RUNNING_URL_PATTERN.test(req.url);
  }

  // Ambient/background calls that should never trigger the global activity bar - showing it for
  // these would make it flicker almost constantly rather than mean something. Centralized here
  // (one place) rather than requiring every call site across the app to opt out individually.
  // Deliberately reuses isHeartbeatEndpoint()/isErrorReportEndpoint() below rather than
  // duplicating those patterns.
  private static readonly SILENT_ACTIVITY_URL_PATTERN =
    /\/(unread-count|analytics|search|autocomplete|suggest|stock-groups|stock-availability)\b/i;

  private isExcludedFromActivityIndicator(req: HttpRequest<any>): boolean {
    return this.isHeartbeatEndpoint(req.url)
      || this.isErrorReportEndpoint(req.url)
      || this.isTicketCountEndpoint(req.url)
      || this.isCustomMadeRunPollEndpoint(req.url)
      || TallysheetHttpInterceptorService.SILENT_ACTIVITY_URL_PATTERN.test(req.url);
  }

  // Ambient background poll (app.component.ts startTicketBadgePolling, 60s/300s), same class of
  // "never mean anything to the user" call as the heartbeats. Deliberately an exact full-path
  // .includes() (matching isHeartbeatEndpoint's convention) rather than folding "count" into the
  // shared SILENT_ACTIVITY_URL_PATTERN regex above — that pattern is loose/shared and a bare
  // "count" term would also wrongly silence real user-facing dashboard loads that share the
  // substring (bills-of-lading/count, containers/count, count-eta, count-etd).
  private isTicketCountEndpoint(url: string): boolean {
    return url?.includes('/api/v1/tickets/count') ?? false;
  }

  // Ambient background poll (tallysheet-square-list.component.ts pollCustomMadeStatuses, 20s),
  // same class as the ticket-count poll above: GET .../processing-runs/{encodedId} with nothing
  // else in the path. encodedId is OpaqueIdGenerator.newToken() server-side (URL-safe Base64,
  // no padding -> [A-Za-z0-9_-] only), so the id-segment character class below is exact, not a
  // guess. The negative lookahead excludes the sibling literal endpoint
  // .../processing-runs/available-inputs and .../in-progress-summary (real user-facing dropdown load, not this poll) which
  // would otherwise match the same "single segment after processing-runs/" shape. Every other
  // processing-runs endpoint (list, create, start, complete, output, tally-search,
  // output-tally-totals) has either no id segment or extra segments after it, so this pattern
  // does not match them.
  private static readonly CUSTOM_MADE_POLL_URL_PATTERN =
    /\/processing-runs\/(?!available-inputs\b|in-progress-summary\b)[A-Za-z0-9_-]+(?:\?|$)/;

  private isCustomMadeRunPollEndpoint(url: string): boolean {
    return TallysheetHttpInterceptorService.CUSTOM_MADE_POLL_URL_PATTERN.test(url ?? '');
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Offline sync only (prefill-store.ts): an answer the sync already fetched in one bundle request is handed to the normal code
    // here, so the sync does not make the same call again. The store is empty except during a sync, so for every other request
    // this is one cheap check; only an exact, fresh, single-use match is served, everything else continues to the network.
    if (req.method === 'GET') {
      const prefilled = takePrefilled(req.urlWithParams);
      if (prefilled.hit) {
        return of(new HttpResponse({ status: 200, statusText: 'OK', url: req.urlWithParams, body: prefilled.body }));
      }
      // A real answer from the server: the offline sync uses this as proof that a call was answered (not a saved copy). Does
      // nothing unless a sync run is going.
      return this.interceptRequest(req, next).pipe(tap((event) => {
        if (event instanceof HttpResponse && event.status === 200) noteAnswered(req.urlWithParams);
      }));
    }
    // An upload (FormData body) is registered while it runs, so pull-down refresh never interrupts it.
    if (req.body instanceof FormData) {
      return defer(() => {
        this.httpActivityService.startUpload();
        return this.interceptRequest(req, next).pipe(finalize(() => this.httpActivityService.endUpload()));
      });
    }
    return this.interceptRequest(req, next);
  }

  private interceptRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // iPhone app only, and only for OUR API (never third-party hosts: an extra header there could fail their CORS check).
    const deviceHeaders = req.url.startsWith(AppConstants.API_URL) ? clientDeviceHeaders() : {};
    const clonedRequest = req.clone({ withCredentials: true, setHeaders: deviceHeaders });

    // Gates the ONLINE 30s-timeout+retry wrapper only (line ~167 below) — long-running
    // endpoints (export/photos/document-content/etc) legitimately need more than 30s when
    // actually online, so they're excluded from that wrapper. Deliberately NOT used for the
    // offline fast-fail check below (isGetToApi) — offline, a "long-running" request is
    // exactly as doomed as any other GET, and excluding it there used to mean it fell through
    // with no timeout wrapper AT ALL, hanging on the native HTTP stack's own default timeout
    // (well over a minute on Android/iOS in airplane mode) instead of failing in ~0ms like
    // every other GET. Confirmed root cause of Product/Loading Site edit forms appearing to
    // never open offline — both eagerly load a photos/documents-content endpoint on init.
    const applyResilience = clonedRequest.method === 'GET' && !this.isLongRunningRequest(clonedRequest);
    const isGetToApi = clonedRequest.method === 'GET';

    // Started once per request here, ended via finalize() on whichever branch below actually
    // returns - success, error, or unsubscription (e.g. navigating away mid-request) all run
    // finalize(), so this can never leak an unmatched start().
    const trackActivity = !this.isExcludedFromActivityIndicator(clonedRequest);
    if (trackActivity) {
      this.httpActivityService.start();
    }
    const endActivity = <T>(obs$: Observable<T>): Observable<T> =>
      trackActivity ? obs$.pipe(finalize(() => this.httpActivityService.end())) : obs$;

    // Offline fast-fail — every GET path in the app already has a catchError fallback keyed
    // on status:0 (cache fallback, offline-lease checks, etc.), reached today only after the
    // 30s timeout + 800ms-delayed retry below actually exhausts itself against a dead network.
    // When the device is known offline, skip straight to that same status:0 failure — same
    // downstream behavior, without the wait. Scoped to GET only: writes already decide
    // their own offline handling explicitly (see TransportUnitService.create/update) rather
    // than relying on this interceptor, so silently skipping a POST here would be wrong.
    //
    // Deliberately every GET (isGetToApi), NOT applyResilience — long-running endpoints must
    // NOT be excluded here even though they're excluded from the timeout+retry wrapper below;
    // see applyResilience's own comment above for why conflating the two used to cause a
    // multi-minute hang instead of an instant fail.
    //
    // Two constraints, both learned the hard way (offline-only app-wide UI freeze):
    // 1. Relative-URL requests are same-origin — in the Capacitor app they're served straight
    //    from the APK's bundled assets (e.g. /assets/loc/countries.json) and succeed with the
    //    radio off. Only absolute (API) URLs actually need the network; never fast-fail assets.
    // 2. The failure MUST be delivered asynchronously (timer(0)). HttpClient errors are always
    //    async, so components freely call .subscribe() with no error callback inside ngOnInit.
    //    A bare throwError() emits synchronously at the subscribe() call site, turning that
    //    missing error callback into an uncaught exception inside Angular's view-creation /
    //    change-detection pass — which wedges change detection for the whole app (page renders
    //    but nothing is clickable until an app resume forces a fresh CD tick).
    const requiresNetwork = /^https?:\/\//i.test(clonedRequest.url);
    if (isGetToApi && requiresNetwork && !this.networkStatusService.isOnline) {
      return endActivity(timer(0).pipe(
        switchMap(() => throwError(() => new HttpErrorResponse({
          status: 0,
          statusText: 'Offline',
          url: clonedRequest.url,
        }))),
        catchError((error: HttpErrorResponse) => this.handleError(error, clonedRequest, next)),
      ));
    }

    let handled$ = next.handle(clonedRequest);
    if (applyResilience) {
      handled$ = handled$.pipe(
        timeout(30_000),
        retry({
          count: 1,
          delay: (error: any) => {
            const status = error?.status;
            // Don't retry client errors (400-499) — only transient network failures (status 0 / timeout) and 5xx.
            if (status !== undefined && status >= 400 && status < 500) {
              return throwError(() => error);
            }
            return timer(800);
          },
        })
      );
    }

    // Feeds NetworkStatusService's slow-connection detection — only for the same GET,
    // non-long-running requests the 2G resilience layer above already applies to (a slow report
    // export shouldn't flag the whole connection as slow). Sampled on success only; a failed/
    // timed-out request is a connectivity problem surfaced elsewhere, not a latency data point.
    const latencyStartedAt = applyResilience ? performance.now() : 0;

    return endActivity(handled$.pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.logger.debug('Interceptor :: Response:', {
            status: event.status,
            url: event.url,
            body: event.body
          });
          if (event.status === 204) {
            this.logger.info('Interceptor :: HTTP 204 No Content received, treated as success');
          }
          if (applyResilience) {
            this.networkStatusService.recordRequestLatency(performance.now() - latencyStartedAt);
          }
        }
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error, clonedRequest, next)),
    ));
  }

  private handleError(error: HttpErrorResponse, clonedRequest: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Background look-ups that are expected to be refused while nobody is signed in: no error line, no refresh, no dialog.
    if (error.status === 401 && (clonedRequest.url || '').toLowerCase().includes('/api/v1/wa-verify/pending')) {
      return throwError(() => error);
    }
    this.logger.error('Interceptor :: Error:', {
      status: error.status,
      url: error.url,
      message: error.message,
      error: error.error
    });
    if (error.status === 429) {
      const retryAfter = error.headers?.get('Retry-After') ?? '60';
      this.logger.warn('Interceptor :: Rate limit exceeded', { url: error.url, retryAfter });
      this.messageService.add({
        severity: 'warn',
        summary: 'Too Many Requests',
        detail: `Rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`,
        life: 8000,
      });
      return throwError(() => error);
    }
    if (error.status === 402 && error.error?.code === 'SUBSCRIPTION_INACTIVE') {
      // Hard lockout (JwtRequestFilter's per-request subscription gate), not the softer numeric
      // plan-limit-reached case below — those share the 402 status but PlanLimitExceededException's
      // response has no "code" field, so this check never matches it. One toast + one redirect
      // regardless of how many concurrent requests on the page independently 402 at once. Kept as
      // a toast (unlike the plan-limit case below) since it's paired with an immediate redirect —
      // the toast is just a "why am I being moved" heads-up, not the whole explanation.
      (error as any).llqHandled = true;
      if (!this.isRedirectingForSubscription) {
        this.isRedirectingForSubscription = true;
        const currentUrl = this.router.url.split('?')[0];
        if (!currentUrl.startsWith('/account-setup/subscription') && !currentUrl.startsWith('/account-setup/payment')) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Subscription Required',
            detail: 'Your subscription has expired. Renew to continue using LumberLinq.',
            life: 9000,
          });
          this.ngZone.run(() => this.router.navigateByUrl('/account-setup/subscription'));
        }
        // Reset shortly after — a genuinely fresh 402 later (e.g. after switching to another
        // expired company) must still be able to trigger this again, not be silently swallowed
        // forever by a flag that only ever gets set.
        setTimeout(() => { this.isRedirectingForSubscription = false; }, 3000);
      }
      return throwError(() => error);
    }
    if (error.status === 403 && error.error?.code === 'PLAN_FEATURE_DISABLED' && clonedRequest.context.get(QUIET_PLAN_BLOCK)) {
      // The caller asked to handle this refusal itself (Quick Search leaves the locked source out). No dialog, and
      // marked handled so no screen adds its own error toast on top.
      (error as any).llqHandled = true;
      return throwError(() => error);
    }
    if (error.status === 402 || (error.status === 403 && error.error?.code === 'PLAN_FEATURE_DISABLED')) {
      // A hard, non-retryable block the user just walked into after filling out a form — a toast
      // is the wrong pattern for this (glanceable/transient, easy to miss or get buried under a
      // component's own generic "Operation failed" fallback toast, which is exactly what happened
      // on the Stock Unit create form, found 2026-08-19). A modal, not a toast: see
      // PlanLimitDialogService + the dialog mounted in app.component.html.
      (error as any).llqHandled = true;
      const message = this.getServerMessage(error) || 'Your current plan does not allow this action.';
      const featureKey = error.error?.featureKey || error.error?.code || 'unknown';
      this.ngZone.run(() => this.planLimitDialogService.open({
        featureKey,
        message,
        used: error.error?.current ?? null,
        limit: error.error?.limit ?? null,
      }));
      return throwError(() => error);
    }
    if (error.status === 403) {
      (error as any).llqHandled = true;
      const message = this.getServerMessage(error) || 'You do not have permission to perform this action.';
      this.ngZone.run(() => this.accessDeniedDialogService.open(message));
      return throwError(() => error);
    }
    if (error.status === 409) {
      return throwError(() => error);
    }
    if (error.status === 204) {
      // Handle 204 as success, return empty observable
      this.logger.warn('Interceptor :: Caught 204 in error block, converting to success');
      return of(new HttpResponse({ status: 204, statusText: 'No Content' }));
    }
    if (error.status === 307 && !this.isRedirecting) {
      this.isRedirecting = true;
      this.logger.warn('Interceptor :: Temporary redirect detected:', error);
      this.router.navigate(['/onboarding/account-details']).finally(() => {
        this.isRedirecting = false;
      });
    } else if (error.status === 401 && error.error?.code === 'SESSION_INVALIDATED_ELSEWHERE') {
      // This device's own session was force-revoked by a login elsewhere (confirm-logout-
      // other-devices) — see JwtRequestFilter's invalid-token branch + ForcedLogoutMarker. Skip
      // the generic refresh-then-session-expired-dialog flow entirely (refreshing would just
      // fail the same way, or worse, silently succeed if this device itself later re-logs-in)
      // and show the dedicated dialog with who/where kicked it out instead.
      this.logger.warn('Interceptor :: Session invalidated by a login elsewhere', {
        url: clonedRequest.url,
        device: error.error?.device,
        location: error.error?.location,
      });
      this.ngZone.run(() => {
        this.forcedLogoutDialogService.open({
          device: error.error?.device || null,
          location: error.error?.location || null,
          kickedAtEpochMillis: error.error?.kickedAtEpochMillis ?? null,
        });
      });
      return EMPTY;
    } else if (error.status === 401 && error.error?.code === 'PAYMENT_SESSION_EXPIRED') {
      // Only ever seen inside the Android payment-handoff Custom Tab (see
      // AndroidPaymentHandoffGuard / TokenService.generatePaymentScopeTokenCookie) — this
      // browser tab has no real login page to usefully redirect to, so skip the normal
      // refresh-then-session-expired-dialog flow entirely and just tell the user to go back
      // to the app and start checkout again.
      this.logger.warn('Interceptor :: Payment-scope session expired', { url: clonedRequest.url });
      this.messageService.add({
        severity: 'warn',
        summary: 'Checkout Session Expired',
        detail: 'Your checkout session expired. Please return to the app and tap Subscribe again.',
        life: 9000,
      });
      return throwError(() => error);
    } else if (error.status === 401 && error.error?.code === 'TENANT_MEMBERSHIP_REVOKED') {
      // This session's active tenant just had its membership revoked (left, or removed)
      // since the token was issued (see JwtRequestFilter's per-request membership check).
      // Don't retry the original request after refreshing — the refresh re-scopes to the
      // user's HOME tenant, and blindly retrying would silently substitute the wrong
      // company's data for whatever this request was asking for. Refresh (to get a valid
      // home-tenant token), then hard-navigate home, same pattern as the existing
      // leave/switch-company success handlers (HeaderComponent.performLeaveCompany/
      // performCompanySwitch).
      this.logger.warn('Interceptor :: Active tenant membership revoked, returning to home company', {
        url: clonedRequest.url,
      });
      this.messageService.add({
        severity: 'warn',
        summary: 'Company Access Removed',
        detail: this.getServerMessage(error) || 'You no longer have access to that company. Returning you to your own company.',
        life: 8000,
      });
      return this.refreshSession().pipe(
        switchMap(() => {
          window.location.href = '/';
          return EMPTY;
        }),
        catchError(() => {
          this.showSessionExpiredDialog(error);
          return EMPTY;
        }),
      );
    } else if (error.status === 401) {
      this.logger.warn('Interceptor :: Unauthorized, attempting refresh before login redirect', {
        url: clonedRequest.url
      });
      if (this.shouldPassThroughUnauthorized(clonedRequest)) {
        this.logger.debug('Interceptor :: 401 passed through without refresh/session dialog', {
          requestUrl: clonedRequest.url,
          currentRoute: this.router.url
        });
        return throwError(() => error);
      }
      if (this.shouldSkipRefresh(clonedRequest)) {
        this.logger.warn('Interceptor :: Refresh skipped for auth/public endpoint', {
          url: clonedRequest.url
        });
        if (this.isLogoutRequest(clonedRequest)) {
          this.logger.debug('Interceptor :: Logout 401 passed back to LogoutService without session dialog');
          return throwError(() => error);
        }
        this.showSessionExpiredDialog(error);
        // Same fix as the refresh-failure branch below (2026-09-06): propagate the real
        // error instead of silently completing via EMPTY. Real second instance found live:
        // api/v1/login/pending-session-device-info matches shouldSkipRefresh() (its URL
        // contains "/api/v1/login" even though it isn't the login endpoint itself), so its
        // 401 never even reaches the refresh-failure branch — it was hitting this EMPTY
        // return directly, which is why ConfirmLogoutDevicesComponent's catchError fallback
        // still never ran even after fixing the other branch.
        return throwError(() => error);
      }
      return this.refreshSession().pipe(
        switchMap(() => {
          this.logger.debug('Interceptor :: Refresh succeeded, retrying original request', {
            url: clonedRequest.url
          });
          this.refreshJustSucceeded = true;
          return next.handle(clonedRequest);
        }),
        catchError((refreshError: HttpErrorResponse) => {
          if (this.refreshJustSucceeded) {
            // The refresh call itself succeeded — the session is genuinely valid. Whatever
            // failed here is the RETRIED original request hitting some unrelated problem
            // (a 500, a 403, a stale-permission 401 on this one endpoint, etc.), never a
            // session expiry. Real bug found 2026-07-26: a background call that 500'd right
            // after a successful refresh was showing "Session Expired" with the raw
            // "Http failure response for https://api.../notifications/unread-count: 500 OK"
            // text — wrong dialog, and it leaked the internal API URL to the user. Route
            // non-401 statuses through the normal per-status handling (server-error dialog,
            // access-denied dialog, toast) instead; 401 keeps its existing access-denied
            // treatment, since the refresh already proved the session itself is fine.
            this.refreshJustSucceeded = false;
            if (refreshError.status === 401) {
              this.logger.warn('Interceptor :: Retry 401 after successful refresh — treating as access-denied, not session expiry', {
                originalUrl: clonedRequest.url
              });
              return throwError(() => refreshError);
            }
            this.logger.warn('Interceptor :: Retry failed after successful refresh — routing to normal error handling, not session expiry', {
              originalUrl: clonedRequest.url,
              retryStatus: refreshError.status
            });
            return this.handleError(refreshError, clonedRequest, next);
          }
          this.refreshJustSucceeded = false;
          this.logger.warn('Interceptor :: Refresh failed, redirecting to login', {
            originalUrl: clonedRequest.url,
            refreshStatus: refreshError.status
          });
          this.showSessionExpiredDialog(refreshError);
          // Real bug found 2026-09-06: this used to return EMPTY unconditionally — an
          // Observable that silently COMPLETES with no value and no error. On a route where
          // showSessionExpiredDialog() suppresses its own visual dialog (confirm-logout-devices,
          // post-login, etc. — routes that have no valid session yet BY DESIGN), the caller's own
          // catchError/error-handler fallback never got a chance to run, since EMPTY isn't an
          // error. ConfirmLogoutDevicesComponent's ngOnInit() got stuck permanently blank this
          // way — its own catchError(() => of(null)) fallback was correctly written but never
          // reachable. Propagating the real error lets every caller's own handling actually run;
          // dialog visibility is still controlled entirely by showSessionExpiredDialog() above,
          // unchanged.
          //
          // 2026-10-02 (the other half): on a NORMAL page, a dead session is ONE event with ONE owner (the Session Expired dialog, or the
          // "signed out elsewhere" dialog) that takes the person to the login page. Opening a Stock Unit fires about 25 calls at once; all of them
          // got the 401 and each caller's own error handling reacted (a failed summary, a cache lookup that found nothing, an unhandled error
          // reported to Sentry): a cascade of side effects for something that is not a bug. These calls now simply wait (NEVER: no value, no error,
          // no completion, so nothing downstream runs, not even a firstValueFrom) until the page is replaced by the login page. Routes that have
          // no valid session BY DESIGN (login, confirm-logout-devices, post-login ...) keep getting the real error, exactly as the 2026-09-06 fix needs.
          if (this.isDeadSessionOwnedByDialog()) {
            this.logger.debug('Interceptor :: Dead session - request parked until the login page replaces this one', { url: clonedRequest.url });
            return NEVER;
          }
          return throwError(() => refreshError);
        })
      );
    } else if (error.status >= 500 && error.status < 600) {
      this.logger.error(`Interceptor :: Server error ${error.status}:`, error.error);
      if (!this.isErrorReportEndpoint(clonedRequest.url) && !this.isHeartbeatEndpoint(clonedRequest.url)) {
        const rawBody = error.error;
        const stackTrace = rawBody
          ? JSON.stringify(rawBody, null, 2).slice(0, 8000)
          : undefined;
        const appCode: number | undefined = rawBody?.appCode;
        const appCodeLabel: string | undefined = rawBody?.appCodeLabel;
        // errorRef (when present) is the backend's own correlation id for this exact exception
        // — GlobalExceptionHandler.handleAll persisted the REAL stack trace against it
        // (BackendExceptionLog). Using it here means a later "Send Report" click resolves to
        // that real trace instead of just this generic JSON body. Falls back to a purely
        // client-side ref for responses with no body at all (e.g. a raw network failure).
        const errorRef: string = rawBody?.errorRef || `ERR-${Date.now().toString(36).toUpperCase()}`;
        this.errorReportService.report('HTTP', error.message || `HTTP ${error.status}`, {
          statusCode: error.status,
          requestUrl: clonedRequest.url,
          stackTrace,
        });
        this.ngZone.run(() => {
          this.serverErrorDialogService.show(
            errorRef,
            error.status,
            stackTrace,
            appCode,
            appCodeLabel,
          );
        });
      }
      return throwError(() => error);
    } else {
      this.logger.error('Interceptor :: Unhandled error:', error);
      if (error.status >= 400 && !this.isErrorReportEndpoint(clonedRequest.url) && !this.isHeartbeatEndpoint(clonedRequest.url)) {
        this.errorReportService.report('HTTP', error.message || `HTTP ${error.status}`, {
          statusCode: error.status,
          requestUrl: clonedRequest.url,
        });
      }
    }
    return throwError(() => error);
  }

  private isErrorReportEndpoint(url: string): boolean {
    return url?.includes('/errors/') ?? false;
  }

  // Heartbeat/health-check pings (ActivityTrackerService, OfflineLeaseService) fail routinely
  // and expectedly during a backend deploy restart — that's not an actionable error for the
  // user. Excluded from the global 500 dialog / error-report pipeline only; each service's own
  // subscribe still gets the error and silently no-ops as it already does. No other endpoint's
  // error handling is affected.
  private isHeartbeatEndpoint(url: string): boolean {
    return url?.includes('/analytics/heartbeat') || url?.includes('/sync/heartbeat') || false;
  }

  private refreshSession(): Observable<any> {
    if (!this.refreshRequest$) {
      this.logger.debug('Interceptor :: Starting shared refresh request');
      this.refreshRequest$ = this.authService.refreshSession().pipe(
        shareReplay(1),
        finalize(() => {
          this.logger.debug('Interceptor :: Shared refresh request finished');
          this.refreshRequest$ = null;
        })
      );
    } else {
      this.logger.debug('Interceptor :: Joining existing refresh request');
    }
    return this.refreshRequest$;
  }

  private shouldSkipRefresh(req: HttpRequest<any>): boolean {
    const url = req.url.toLowerCase();
    return url.includes('/auth/refresh')
      || url.includes('/api/v1/login')
      || url.includes('/auth/login')
      || url.includes('/auth/signup')
      || url.includes('/api/v1/user-logout')
      // Fired best-effort from LogoutService.clearClientSession() AFTER /api/v1/user-logout has
      // already blacklisted the session — this call is expected to 401 by design (server-side
      // logout now does the real device-token deactivation synchronously). Don't let a doomed
      // refresh attempt race the navigate-to-/login transition or the session-expired dialog.
      || url.includes('/api/v1/push/devices/unregister')
      || url.includes('/openforall/')
      || url.includes('/api/v1/password/')
      || url.includes('/api/v1/verify-email')
      || url.includes('/api/v1/auth/pre-register')
      // Pre-auth by definition — a failed/expired mobile OAuth exchange code should surface
      // as a normal error to MobileOAuthCallbackService, not trigger a pointless refresh
      // attempt (there's no session yet) followed by a confusing "session expired" dialog.
      || url.includes('/api/v1/auth/mobile/token-exchange');
  }

  private shouldPassThroughUnauthorized(req: HttpRequest<any>): boolean {
    const url = req.url.toLowerCase();
    if (url.includes('/openforall/api/v1/login/is-user-loggedin')) return true;
    // Analytics fire-and-forget endpoints — never trigger session dialog on 401
    if (url.includes('/api/v1/analytics/')) return true;
    // Until the router has committed to a protected route at least once in this session,
    // the user is not logged in and every 401 is just a startup probe (Header, Sidebar,
    // LoginRedirectGuard probing /permissions). Attempting a token refresh here will always
    // fail and falsely show the Session Expired dialog on a fresh incognito load.
    //
    // BUT a cold/direct load (typed URL, hard refresh, or a native app relaunch after the
    // OS killed the process) also has authenticatedRouteEstablished === false on its very
    // FIRST guarded navigation, since NavigationEnd can't have fired yet — even for a user
    // with a perfectly valid, just-expired-access-token session. Real bug found 2026-08-03:
    // AuthSessionGuard's own permission check 401s on that first navigation, this check waved it
    // through without attempting a refresh, and PermissionService.syncSessionForGuard()
    // fail-opened to an empty permission set — PermissionGuard then denied every gated
    // route and redirected to /unauthorized. hasEverLoggedInOnThisDevice() closes that gap:
    // if this device has completed a real login before (even in a prior session), a 401 is
    // worth a refresh attempt regardless of router lifecycle state.
    if (!this.authenticatedRouteEstablished && !this.hasEverLoggedInOnThisDevice()) return true;
    // All public-facing pages are accessed by unauthenticated viewers — never trigger session refresh.
    // Also check getCurrentNavigation() because guards fire before router.url commits to the new route,
    // causing a race where a fresh visit to /login still shows router.url as "/".
    const current = this.router.url;
    const pending = this.router.getCurrentNavigation()?.extractedUrl?.toString() ?? '';
    // Safety net: Angular initialises router.url as '/' before any navigation commits.
    // If we are still at that default AND no navigation is in progress, this 401 came
    // from a component that rendered before the router established context (e.g. the
    // Header or Sidebar firing startup probes). The user is definitely not logged in,
    // so there is nothing to refresh — pass through silently.
    if (current === '/' && !pending) {
      return true;
    }
    const isSuppressed = (r: string) =>
      r.startsWith('/s/')
      || r.startsWith('/tally-sheet/share')
      || r.startsWith('/tallysheet-view')
      || r.startsWith('/public/view/')
      || r.startsWith('/public/live/')
      || r === '/s'
      || r.startsWith('/login');
    return isSuppressed(current) || isSuppressed(pending);
  }

  // Long-lived, non-expiring, presence-only signal — set by AuthSessionGuard.wipeOfflineDataIfDifferentUser
  // only after a genuine server-confirmed successful permission sync, and deliberately preserved
  // across localStorage.clear() on normal logout (see LogoutService.clearClientSession). Unlike the
  // access-token/x_client_id cookie (same short TTL as the access token — useless here since it's
  // also gone in the exact scenario this is meant to catch), this has no TTL coupling at all.
  private hasEverLoggedInOnThisDevice(): boolean {
    return !!localStorage.getItem(AppConstants.LAST_DEVICE_USER_EMAIL);
  }

  private isLogoutRequest(req: HttpRequest<any>): boolean {
    return req.url.toLowerCase().includes('/api/v1/user-logout');
  }

  private showSessionExpiredDialog(error: HttpErrorResponse): void {
    // This device has already been told it was signed out by a login elsewhere, with the device
    // and location that did it. The backend kick marker is consume-once, so the OTHER concurrent
    // 401s from the same dead session arrive here with no code and would stack a vaguer "Session
    // Expired" dialog on top of that one, hiding it. The forced-logout dialog is strictly more
    // informative and is the only one with a correct explanation — let it stand.
    if (this.forcedLogoutDialogService.isActive()) {
      this.logger.debug('Interceptor :: Session expired dialog suppressed — forced-logout-elsewhere dialog owns this session');
      return;
    }
    if (this.sessionExpiredDialogVisible) {
      this.logger.debug('Interceptor :: Session expired dialog already pending, skipping duplicate');
      return;
    }

    // Set flag immediately so concurrent 401 callers don't each try to open a dialog.
    // It will be reset to false if the deferred route check suppresses this dialog.
    this.sessionExpiredDialogVisible = true;
    const message = this.getSessionExpiredMessage();

    // Defer the route check by one tick so the Angular router has time to commit
    // to its destination route (e.g. /login). Without this deferral, router.url can
    // still be '/' during the redirect from '' → /login, causing a false positive.
    setTimeout(() => {
      const pendingRoute = this.router.getCurrentNavigation()?.extractedUrl?.toString() ?? '';
      if (
        this.router.url.includes('/login') ||
        pendingRoute.startsWith('/login') ||
        this.isSessionExpiredDialogSuppressedRoute() ||
        // Re-check here, not just at the top of the outer function: the forced-logout signal
        // (a sibling 401 that DOES carry the kick marker) can arrive in the gap between this
        // deferred tick being scheduled and it actually firing, since both originate from the
        // same burst of concurrent requests off a dead session.
        this.forcedLogoutDialogService.isActive()
      ) {
        this.logger.debug('Interceptor :: Session expired dialog suppressed (deferred check)', {
          currentUrl: this.router.url,
          pendingRoute,
          suppressedRoute: this.isSessionExpiredDialogSuppressedRoute()
        });
        // Reset flag so a genuine session expiry on a protected route can still show
        // the dialog later in the same session.
        this.sessionExpiredDialogVisible = false;
        return;
      }

      this.logger.warn('Interceptor :: Showing session expired dialog', {
        status: error.status,
        message
      });
      // The session is dead: the background offline sync must not keep running (or show its strip) behind this dialog.
      cancelOfflineSync('session-expired');
      this.ngZone.run(() => {
        this.sessionExpiredDialogService.open({
          message,
          onAccept: () => {
            this.logger.info('Interceptor :: Session expired dialog accepted, clearing local session and navigating login');
            this.sessionExpiredDialogVisible = false;
            // The next login must pass the different-user check again before any queued offline
            // work syncs (OFFLINE_PLAN.md, 2026-09-29) — waiting work is kept either way.
            resetOfflineIdentityVerified();
            // This path never calls /api/v1/user-logout (the session is already invalid, there's
            // nothing to notify), so it's also the one logout-adjacent path that never triggers
            // the backend's own device-token deactivation. Fire this directly so the FCM/GCM
            // device token doesn't stay marked active indefinitely — safe to call even though the
            // session is dead, since /api/v1/push/devices/unregister no longer requires auth.
            this.pushNotificationService.unregisterCurrentToken().catch((err) => this.logger.warn('Interceptor :: Failed to unregister push token', err));
            const tourEntries: [string, string][] = [];
            for (let i = 0; i < localStorage.length; i++) {
              const k = localStorage.key(i);
              if (k?.startsWith('tms_tour_')) tourEntries.push([k, localStorage.getItem(k)!]);
            }
            // Preserved across the clear() for the same reason LogoutService.clearClientSession
            // preserves them: this path deliberately KEEPS the offline read cache below, so it
            // must also keep the identity evidence that guards it. Dropping these while retaining
            // the cache would leave the next login unable to detect a different user/tenant on
            // this device — the cache would silently survive into someone else's session.
            const lastDeviceUserEmail = localStorage.getItem(AppConstants.LAST_DEVICE_USER_EMAIL);
            const lastDeviceTenantId = localStorage.getItem(AppConstants.LAST_DEVICE_TENANT_ID);
            localStorage.clear();
            sessionStorage.clear();
            tourEntries.forEach(([k, v]) => localStorage.setItem(k, v));
            if (lastDeviceUserEmail) {
              localStorage.setItem(AppConstants.LAST_DEVICE_USER_EMAIL, lastDeviceUserEmail);
            }
            if (lastDeviceTenantId) {
              localStorage.setItem(AppConstants.LAST_DEVICE_TENANT_ID, lastDeviceTenantId);
            }
            // The offline READ cache is intentionally left in place here, matching
            // LogoutService.clearClientSession — a session that just failed to refresh is, in
            // the common case, the SAME user being forced to re-login, and they should keep
            // their cached data rather than starting from a cold cache. It's only ever cleared
            // by AuthSessionGuard.wipeOfflineDataIfDifferentUser, on a genuinely different email/tenant.
            // The offline-grace lease itself is intentionally left alone here too; it's
            // invalidated only via its own refresh-token rejection path (see OfflineLeaseService).
            this.router.navigateByUrl('/login?sessionExpired=true');
          },
          onReject: () => {
            this.logger.info('Interceptor :: Session expired dialog rejected, remaining on current page');
            this.sessionExpiredDialogVisible = false;
          }
        });
      });
    });
  }

  // Real bug found 2026-07-26: this used to surface the raw HttpErrorResponse text (internal API
  // URL, HTTP status, backend exception messages) directly in a user-facing dialog — e.g. "Http
  // failure response for https://api.lumberlinq.com/api/v1/notifications/unread-count: 500 OK".
  // A session timing out is a routine, expected event, not something the user needs (or should
  // see) technical detail about — one clean, calm, translated message every time. The triggering
  // error is still fully captured via this.logger.warn() at the call site for diagnostics.
  private getSessionExpiredMessage(): string {
    return this.translationService.instant('auth.sessionExpiredMessage');
  }

  private getServerMessage(error: HttpErrorResponse): string {
    const errorBody = error.error;
    if (typeof errorBody === 'string') {
      return errorBody;
    }
    return errorBody?.message || errorBody?.error || error.message || '';
  }

  private isRouteSuppressedForSessionDialog(url: string): boolean {
    const normalised = url.split('?')[0].split('#')[0];
    const suppressedRoutes = [
      '/login',
      '/registration',
      '/password-reset',
      '/email-verification-prompt',
      '/email-verification',
      '/tally-sheet/confirm-logout-devices',
      '/pagenotfound',
      '/error/',
      '/unauthorized',
      '/restrictedPage',
      '/payment/success',
      '/payment/failure',
      '/payment-confirmation',
      '/payment-error',
      '/user-added-to-existing-company/',
      '/post-login',
      '/invited-user',
      '/tally-sheet/share',
      '/tallysheet-view',
      '/s/',
      '/public/view/',
      '/public/live/',
    ];
    return suppressedRoutes.some(r => normalised.startsWith(r));
  }

  /** True while a dead session on a normal page is being handled by a dialog that leads to the login page (see the refresh-failure branch). */
  private isDeadSessionOwnedByDialog(): boolean {
    if (this.forcedLogoutDialogService.isActive()) return true;       // the "signed out elsewhere" dialog owns it
    const pending = this.router.getCurrentNavigation()?.extractedUrl?.toString() ?? '';
    if (this.router.url.includes('/login') || pending.startsWith('/login')) return false;   // already on the way to login: nothing to park
    return !this.isSessionExpiredDialogSuppressedRoute();
  }

  private isSessionExpiredDialogSuppressedRoute(): boolean {
    return this.isRouteSuppressedForSessionDialog(this.router.url);
  }
}
/*@Injectable()
export class TallysheetHttpInterceptorService implements HttpInterceptor {
  constructor(private router: Router) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const clonedRequest = req.clone({ withCredentials: true });
    let isRedirecting = false;

    return next.handle(clonedRequest).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          console.log('Interceptor :: Response:', event);
        }
      }),
      catchError((error: any) => {
        console.error('Error intercepted:', error);
        if (error.status === 307 && !isRedirecting) {
          isRedirecting = true; // Prevent multiple redirects
          console.log('Temporary redirect detected:', JSON.stringify(error));
          this.router.navigate(['/onboarding/account-details']).finally(() => {
            isRedirecting = false; // Reset flag after navigation
          });
        }
        else if (error.status === 401) {
          // alert('You are not logged in or your session has expired.');
        } else if (error.status === 500) {
          //alert('Internal server error. Please try again later.');
        }
        // Re-throw the original error with its complete structure
        return throwError(() => error);
      })
    );
  }
}*/
/*
@Injectable()
export class TallysheetHttpInterceptorService implements HttpInterceptor {

  constructor(private authService: AuthService, private router: Router) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Get the token from AuthService

    // Clone the request to modify the headers

    let clonedRequest = req.clone({
      withCredentials: true // Include credentials
    });

    // Log the headers to the console (optional for debugging)
    const headersInfo = clonedRequest.headers.keys().map(key => {
      const headerString = `${key}: ${clonedRequest.headers.get(key)}`;
      console.log(headerString);  // This logs each header.
      return headerString;        // This returns the header string to the new array.
    });

    console.log('Interceptor :: Request Headers:', headersInfo);


    // Check if the method is POST and log the body
    if (req.method === 'POST') {
      console.log('POST Request Body:', req.body);
    }

    // Pass the cloned request to the next handler
    return next.handle(clonedRequest).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          // Log the response to the console
          console.log('Interceptor :: Response:', event);
        }
      }),
      catchError((error: any) => {
        console.error(`Error is ${JSON.stringify(error.error)}`);
        let errorMessage = '';

        // Client-side error
        if (error.error instanceof ErrorEvent) {
          errorMessage = `Client Error: ${error.error.message}`;
        }
        // Server-side error
        else {
          errorMessage = `Server Error: ${error.status} - ${error.message}`;
        }

        const err = error.error;
        let count = 0;
        if (err) {
          const httpStatusCode = err.httpStatusCode;
          const httpStatus = err.httpStatus;
          const url = err.url;
          const data = err.data;
          if (error.status === 0) {
            // Trigger an alert when the connection is refused
            if (count === 0) {
              alert('Connection refused: Please check your network or server is down.');
            }
            count++;
          }
          // console.error('Error occurred:', JSON.stringify(err));
          console.error(`httpStatusCode {} httpStatus {} url{} data {}`, httpStatusCode, httpStatus, url, JSON.stringify(data))
          this.router.navigate([url], { queryParams: { data: JSON.stringify(data) } });

          // Custom logic for specific error codes
          if (error.status === 403) {
            //  this.router.navigateByUrl(url);
          } else if (error.status === 401) {
            // this.router.navigate(['/login']);
          }
        }
        // You can throw the error or return a default fallback response
        // return throwError(() => error);
        return of(null); // or return of({}); if you want to return an empty object

      })
    );
  }
}
*/
